import type { Experiment } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import moment from 'moment'
import type { ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import { isSocialProofPopupSuppressed, recordSocialProofPopupExposure } from 'storage/socialProofPopup'
import { SOCIAL_PROOF_POPUP_TEMPORARILY_DISABLED } from './socialProofPopupKillSwitch'
import GeoContext from 'contexts/geoContext'
import { selectSocialProofPurchase, SOCIAL_PROOF_MAX_AGE_MS } from './socialProofPurchaseUtils'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import HideVisually from 'components/Common/HideVisually'
import styled from 'styled-components'

const SHOW_DELAY_MS = 1500
const SOCIAL_PROOF_DURATION_MS = 8000

const InlineLuxPlusLogo = styled.span`
  margin-inline: ${rem(2)} ${rem(3)};
`

export interface SocialProofPopupDialog {
  message: ReactNode
  offerImage: App.Image
  isLuxPlusPurchase: boolean
}

interface RecentPurchaseSocialProof {
  isWithinMaxAge: boolean
  purchaseCountry?: string
  purchasedAt?: string
  purchaseKey?: string
}

interface TimerRef {
  current: number | undefined
}

export const SOCIAL_PROOF_COPY_VARIANTS = {
  flashHotel: 'flashHotel',
  sppStay: 'sppStay',
  tour: 'tour',
} as const

export type SocialProofCopyVariant =
  (typeof SOCIAL_PROOF_COPY_VARIANTS)[keyof typeof SOCIAL_PROOF_COPY_VARIANTS]

interface UseSocialProofPopupOptions {
  copyVariant: SocialProofCopyVariant
  experiment: Experiment
  offerImage: App.Image
  recentPurchases: Array<App.OfferRecentPurchase> | undefined
}

function getPurchaseKey(purchaseCountry: string | undefined, purchasedAt: string | undefined) {
  return purchaseCountry && purchasedAt ? `${purchaseCountry}:${purchasedAt}` : undefined
}

function getRecentPurchaseSocialProof(recentPurchases: Array<App.OfferRecentPurchase> | undefined, websiteRegion: string): RecentPurchaseSocialProof {
  const recentPurchase = selectSocialProofPurchase(recentPurchases, websiteRegion, Date.now())
  const purchaseCountry = recentPurchase?.country
  const purchasedAt = recentPurchase?.purchasedAt

  return {
    isWithinMaxAge: !!recentPurchase,
    purchaseCountry,
    purchasedAt,
    purchaseKey: getPurchaseKey(purchaseCountry, purchasedAt),
  }
}

function canActivateRecentPurchaseSocialProof({ isWithinMaxAge, purchaseCountry, purchasedAt, purchaseKey }: RecentPurchaseSocialProof) {
  return !!purchaseKey && !!purchaseCountry && !!purchasedAt && isWithinMaxAge
}

function getCountryName(locale: string, countryCode: string) {
  if (typeof Intl.DisplayNames !== 'function') {
    return countryCode
  }

  try {
    const countryNames = new Intl.DisplayNames([locale], { type: 'region' })

    return countryNames.of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

function renderLuxPlusLogo(chunks: Array<ReactNode>) {
  return <InlineLuxPlusLogo key="lux-plus-logo">
    <LuxPlusLogo inline colour="inherit" height={12} width={89} aria-hidden="true" />
    <HideVisually>{chunks}</HideVisually>
  </InlineLuxPlusLogo>
}

function formatSocialProofMessage(intl: IntlShape, purchaseCountry: string, purchasedAt: string, copyVariant: SocialProofCopyVariant, isLuxPlusPurchase: boolean) {
  const countryName = getCountryName(intl.locale, purchaseCountry)
  // Always format minutes: automatic relative-time units round 45+ minutes to an hour.
  const minutesAgo = moment().diff(purchasedAt, 'minutes')
  const timeAgo = intl.formatRelativeTime(-minutesAgo, 'minute', { numeric: 'always' })

  if (isLuxPlusPurchase) {
    return intl.formatMessage(
      {
        id: 'l6OeFPuttCWD80hnh03f',
        defaultMessage: 'A <luxPlusLogo>LUXPLUS+</luxPlusLogo> member in {countryName} purchased this offer {timeAgo}.',
        description: 'Social proof notification describing a member purchase. The luxPlusLogo tag replaces the brand name with its inline logo and preserves the text for screen readers.',
      },
      { countryName, timeAgo, luxPlusLogo: renderLuxPlusLogo },
    )
  }

  if (copyVariant === SOCIAL_PROOF_COPY_VARIANTS.sppStay) {
    return intl.formatMessage(
      {
        id: 'nFUqkv0W5BLYIWhAoIMk',
        defaultMessage: 'Someone from {countryName} booked this stay {timeAgo}.',
        description: 'Social proof popup message on SPP accommodation property pages showing where and when a recent stay booking happened.',
      },
      { countryName, timeAgo },
    )
  }

  if (copyVariant === SOCIAL_PROOF_COPY_VARIANTS.tour) {
    return intl.formatMessage(
      {
        id: '4W9xUIQ3Is8hgkgz181q',
        defaultMessage: 'Someone from {countryName} booked this tour {timeAgo}.',
        description: 'Social proof popup message on tour offer pages showing where and when a recent tour booking happened.',
      },
      { countryName, timeAgo },
    )
  }

  return intl.formatMessage(
    {
      id: 'hsJIsh7AXvZkocH0b0P1',
      defaultMessage: 'Someone from {countryName} purchased this hotel {timeAgo}.',
      description: 'Social proof popup message on accommodation offer pages showing where and when a recent booking happened.',
    },
    { countryName, timeAgo },
  )
}

function clearDismissTimer(dismissTimerRef: TimerRef) {
  window.clearTimeout(dismissTimerRef.current)
  dismissTimerRef.current = undefined
}

function scheduleDismissTimer(dismissTimerRef: TimerRef, dismissDialog: () => void, purchasedAt: string) {
  clearDismissTimer(dismissTimerRef)
  const expiresAt = moment(purchasedAt).add(SOCIAL_PROOF_MAX_AGE_MS, 'milliseconds')
  // Exactly 59 minutes is still eligible; dismiss one millisecond after that boundary.
  const remainingFreshnessMs = expiresAt.diff(moment(), 'milliseconds') + 1
  const dismissDelayMs = Math.min(SOCIAL_PROOF_DURATION_MS, remainingFreshnessMs)

  dismissTimerRef.current = window.setTimeout(() => {
    dismissTimerRef.current = undefined
    dismissDialog()
  }, dismissDelayMs)
}

function useSocialProofPopup({
  copyVariant,
  experiment,
  offerImage,
  recentPurchases,
}: UseSocialProofPopupOptions) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const offerImageRef = useRef(offerImage)
  const displayedPurchaseKeyRef = useRef<string | undefined>(undefined)
  const dismissTimerRef = useRef<number | undefined>(undefined)
  const [dialog, setDialog] = useState<SocialProofPopupDialog | undefined>(undefined)
  const recentPurchaseSocialProof = useMemo(() => getRecentPurchaseSocialProof(recentPurchases, currentRegionCode), [recentPurchases, currentRegionCode])
  const {
    purchaseCountry,
    purchasedAt,
    purchaseKey,
  } = recentPurchaseSocialProof
  const [isSuppressed, setIsSuppressed] = useState(true)
  const canActivateSocialProof = !SOCIAL_PROOF_POPUP_TEMPORARILY_DISABLED &&
    canActivateRecentPurchaseSocialProof(recentPurchaseSocialProof) &&
    !isSuppressed
  const {
    isTurnedOn: isSocialProofTurnedOn,
    isAnyVariant: isSocialProofEnabled,
  } = useExperiment(experiment, { canActivate: canActivateSocialProof })

  useEffect(() => {
    offerImageRef.current = offerImage
  }, [offerImage])

  const hideDialog = useCallback(() => {
    setDialog(undefined)
  }, [])

  const dismissDialog = useCallback(() => {
    clearDismissTimer(dismissTimerRef)
    hideDialog()
  }, [hideDialog])

  useEffect(() => {
    return () => {
      clearDismissTimer(dismissTimerRef)
    }
  }, [])

  useEffect(() => {
    setIsSuppressed(!purchaseKey || isSocialProofPopupSuppressed())
  }, [purchaseKey])

  useEffect(() => {
    if (!canActivateSocialProof || !isSocialProofTurnedOn || !purchaseKey || !purchaseCountry || !purchasedAt || displayedPurchaseKeyRef.current === purchaseKey) return

    const timer = window.setTimeout(() => {
      // Re-evaluate at exposure time: purchases can expire during the show delay.
      const purchase = selectSocialProofPurchase(recentPurchases, currentRegionCode, Date.now())
      if (!purchase) return
      const selectedPurchaseKey = getPurchaseKey(purchase.country, purchase.purchasedAt)
      if (displayedPurchaseKeyRef.current === selectedPurchaseKey) return

      if (isSocialProofPopupSuppressed()) {
        setIsSuppressed(true)
        return
      }

      displayedPurchaseKeyRef.current = selectedPurchaseKey
      recordSocialProofPopupExposure()
      setIsSuppressed(true)

      if (!isSocialProofEnabled) return

      setDialog({
        message: formatSocialProofMessage(intl, purchase.country.trim().toUpperCase(), purchase.purchasedAt, copyVariant, purchase.isLuxPlusPurchase === true),
        offerImage: offerImageRef.current,
        isLuxPlusPurchase: purchase.isLuxPlusPurchase === true,
      })
      scheduleDismissTimer(dismissTimerRef, hideDialog, purchase.purchasedAt)
    }, SHOW_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [canActivateSocialProof, copyVariant, currentRegionCode, hideDialog, intl, isSocialProofEnabled, isSocialProofTurnedOn, purchaseCountry, purchaseKey, purchasedAt, recentPurchases])

  return useMemo(() => ({
    dialog,
    dismissDialog,
  }), [dialog, dismissDialog])
}

export default useSocialProofPopup
